'use client'

import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox'
import { useId } from 'react'
import { useFieldControl } from './field'
import type { TestIdProps } from '../internal/test-id'

/**
 * A checkbox, from Base UI.
 *
 * Base UI handles the indeterminate state, the hidden native input that makes it
 * submit inside a form, and the space-key behaviour. Hand-rolled checkboxes
 * usually get the first two wrong.
 *
 * The label lives here rather than in Field, because a checkbox reads as
 * "[box] I agree" on one line, not as a label above a control.
 */
export type CheckboxProps = TestIdProps & {
    /** Text beside the box. Required, and always passed in: the package ships no copy. */
    label: string
    checked?: boolean
    defaultChecked?: boolean
    onCheckedChange?: (checked: boolean) => void
    /**
     * Base UI models the mixed state as its own prop rather than a third value
     * of `checked`, which keeps the callback a plain boolean.
     */
    isIndeterminate?: boolean
    isDisabled?: boolean
    /** Hides the label visually while keeping it announced, for table cells. */
    isLabelHidden?: boolean
    name?: string
    value?: string
}

export const Checkbox = ({
    label,
    checked,
    defaultChecked,
    onCheckedChange,
    isIndeterminate = false,
    isDisabled = false,
    isLabelHidden = false,
    name,
    value,
    'data-testid': testId
}: CheckboxProps) => {
    const field = useFieldControl()
    // Standalone is a supported case, so the control owns an id when there is no
    // Field to give it one. Without this the label is unassociated and clicking
    // it does nothing, which is invisible until someone tries.
    const fallbackId = useId()
    const id = field?.controlId ?? fallbackId

    return (
        <div data-testid={testId} className="flex items-center gap-2">
            <CheckboxPrimitive.Root
                id={id}
                name={name}
                value={value}
                checked={checked}
                defaultChecked={defaultChecked}
                // Base UI passes (checked, eventDetails). Normalise to the
                // boolean our public type promises, so callers never see the
                // primitive's event object leak through.
                onCheckedChange={(checked) => onCheckedChange?.(checked)}
                indeterminate={isIndeterminate}
                disabled={isDisabled}
                aria-describedby={field?.describedBy}
                aria-invalid={field?.isInvalid}
                className="border-border bg-background data-checked:bg-background-brand data-checked:border-border-brand data-indeterminate:bg-background-brand data-indeterminate:border-border-brand focus-visible:ring-ring aria-[invalid=true]:border-border-danger-strong flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-aria-sm border transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none data-disabled:cursor-not-allowed data-disabled:opacity-50"
            >
                <CheckboxPrimitive.Indicator className="text-foreground-brand flex items-center justify-center">
                    <svg viewBox="0 0 10 10" className="size-2.5" aria-hidden focusable="false">
                        <path
                            d="M1 5.5 3.5 8 9 2"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </CheckboxPrimitive.Indicator>
            </CheckboxPrimitive.Root>
            <label
                htmlFor={id}
                className={
                    isLabelHidden ? 'sr-only' : 'text-foreground cursor-pointer text-aria-base leading-none select-none'
                }
            >
                {label}
            </label>
        </div>
    )
}
